import { useState, useContext, useMemo } from "react";
import { Box, Button } from "@mui/material";
import { Layer } from "../../types/layer";
import { DrawingContext, DrawingMode, DrawingFeatureType } from "../../contexts/DrawingContext";
import LayerControlItem from "./LayerControlItem";
import FilterModal, { Filter } from "./FilterModal";
import BoltPlusIcon from "../icons/BoltPlusIcon";
import { getAttributesFromLayer } from "../../utils/layerFilters";
import { useAuth } from "../../contexts/AuthContext";
import { isViewOnly } from "../../utils/permissions";

interface IgnitionsControlProps {
  layer: Layer | null;
  filters: Filter[];
  onFiltersChange: (filters: Filter[]) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const IgnitionsControl: React.FC<IgnitionsControlProps> = ({ layer, filters, onFiltersChange, expanded, onExpandedChange }) => {
  const { drawingMode, setDrawingMode, drawingFeatureType, setDrawingFeatureType } = useContext(DrawingContext);
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const attributes = useMemo(() => {
    return layer?.rawSource ? getAttributesFromLayer(layer.rawSource) : [];
  }, [layer?.rawSource]);

  if (!layer) return null;

  const handleToggleVisibility = () => {
    layer.toolbarButton?.props?.onClick?.();
  };

  const handleDrawClick = (featureType: string) => {
    if (drawingMode === DrawingMode.IGNITION) {
      setDrawingMode(null);
      setDrawingFeatureType(null);
    } else {
      setDrawingMode(DrawingMode.IGNITION);
      setDrawingFeatureType(featureType);
    }
  };

  const handleFiltersChange = (newFilters: Filter[]) => {
    onFiltersChange(newFilters);
  };

  const drawingContent = viewOnly ? null : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.IGNITION ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.POINT)}
        sx={{
          color: drawingMode === DrawingMode.IGNITION ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.IGNITION ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.IGNITION ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Draw Point
      </Button>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.IGNITION ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.LINE)}
        sx={{
          color: drawingMode === DrawingMode.IGNITION ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.IGNITION ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.IGNITION ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Draw Line
      </Button>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.IGNITION ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.POLYGON)}
        sx={{
          color: drawingMode === DrawingMode.IGNITION ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.IGNITION ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.IGNITION ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Draw Polygon
      </Button>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.IGNITION && drawingFeatureType === DrawingFeatureType.UPLOAD ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.UPLOAD)}
        sx={{
          color: drawingMode === DrawingMode.IGNITION && drawingFeatureType === DrawingFeatureType.UPLOAD ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.IGNITION && drawingFeatureType === DrawingFeatureType.UPLOAD ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.IGNITION && drawingFeatureType === DrawingFeatureType.UPLOAD ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Upload from File
      </Button>
    </Box>
  );

  return (
    <>
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Ignitions"
        attributes={attributes}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />
      
      <LayerControlItem
        title="Ignitions"
        icon={<BoltPlusIcon />}
        visible={layer.active ?? false}
        onToggleVisibility={handleToggleVisibility}
        enabled={!!layer.rawSource}
        filterCount={filters.length}
        onFilterClick={() => setFilterModalOpen(true)}
        filterDisabled={attributes.length === 0}
        filterDisabledTooltip="No data loaded — add ignitions to currently selected regime to enable filters"
        drawingContent={drawingContent}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
      />
    </>
  );
};

export default IgnitionsControl;
